import { useMutation } from '@apollo/client';
import { Button, message, Modal } from 'antd';
import React from 'react';
import {
  FALLBACK_PDF_URL,
  FIELD_TYPES,
} from '../../../../../../common/constants';
import { DELETE_REGISTER_LOG_DETAIL_ENTRY } from '../../../../graphql/Mutation';

const DeleteFormFieldRecordModal = ({
  showModal,
  setShowModal,
  selectedRecordToDelete,
  setSelectedRecordToDelete,
  tableHeaders,
  tableData,
  setTableData,
  fieldsData,
  form,
  showRouterPrompt,
  fileListRef,
  handleFieldsDataRefUpdate,
  handleFileListRefUpdate,
}) => {
  const [deleteRegisterLogFormFieldEntry] = useMutation(
    DELETE_REGISTER_LOG_DETAIL_ENTRY,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted() {
        const requiredFields = tableHeaders?.filter(
          (field) => field?.isRequired,
        );
        const filteredTableData = tableData?.filter(
          (item) => item?.id !== selectedRecordToDelete?.id,
        );
        const updatedFieldsData = filteredTableData?.map((item) => {
          if (item?.isEmptyRow) {
            const originalIndex = tableData?.findIndex(
              (row) => row?.key === item?.key,
            );
            return fieldsData?.current?.[originalIndex] || item;
          }
          return item;
        });
        handleFieldsDataRefUpdate([...updatedFieldsData]);
        const allKeys = new Set(tableHeaders.map((field) => field?.dataIndex));
        const currentFormValues = form?.getFieldsValue();
        const updatedData = filteredTableData?.map((item, index) => {
          const newObj = {};
          allKeys?.forEach((key) => {
            if (item?.isEmptyRow) {
              const originalIndex = tableData?.findIndex(
                (row) => row?.key === item?.key,
              );
              const originalFieldKey = `${originalIndex}_${key}`;
              newObj[`${index}_${key}`] =
                currentFormValues?.[originalFieldKey] ?? null;
            } else {
              newObj[`${index}_${key}`] = item[key] ?? null;
            }
          });
          return newObj;
        });
        const combinedObject = Object?.assign({}, ...updatedData);
        form?.setFieldsValue(combinedObject);
        const attachmentFields = tableHeaders?.filter(
          (field) => field?.type === FIELD_TYPES.ATTACHMENT,
        );
        const updatedFileList = {};
        filteredTableData?.forEach((row, index) => {
          attachmentFields?.forEach((field) => {
            const key = `${index}_${field?.index}`;
            const updatedValues = (row?.[field?.index] || [])?.map(
              (item, idx) => {
                return {
                  uid: `${key}-${idx}`,
                  status: 'done',
                  url: item?.value || item?.url || item?.thumbUrl,
                  key: item?.key,
                  name:
                    item?.name ||
                    (item?.key ? item?.key?.split('/')?.pop() : undefined),
                  thumbUrl:
                    item?.key?.split('.')?.pop() === 'pdf'
                      ? FALLBACK_PDF_URL
                      : item?.value || item?.url || item?.thumbUrl,
                };
              },
            );
            handleFileListRefUpdate(updatedValues, key);
          });
        });
        const updatedList = {
          ...fileListRef?.current,
          ...updatedFileList,
        };
        handleFileListRefUpdate(updatedList);
        setTableData([...filteredTableData]);
        setSelectedRecordToDelete();
        const hasEmptyRequiredFields = filteredTableData
          ?.slice(0, -1)
          ?.some((row) =>
            requiredFields?.some((field) => {
              const value = row?.[field?.dataIndex];
              return (
                value === null ||
                value === undefined ||
                value === '' ||
                (Array?.isArray(value) && value?.length === 0)
              );
            }),
          );
        showRouterPrompt(hasEmptyRequiredFields);
        setShowModal(false);
      },
    },
  );

  const handleOk = async () => {
    if (!selectedRecordToDelete?.id) {
      message.destroy();
      message.error('You cannot delete a new record');
      setShowModal(false);
      return;
    }
    await deleteRegisterLogFormFieldEntry({
      variables: {
        deleteRegisterLogFormFieldEntryId: selectedRecordToDelete?.id,
      },
    });
  };

  const handleCancel = () => {
    setSelectedRecordToDelete();
    setShowModal(false);
  };

  return (
    <div>
      <Modal
        maskClosable={false}
        centered
        closable={false}
        footer={null}
        open={showModal}
      >
        <h2>Delete Record</h2>
        <p>Are you sure you want to delete this record?</p>
        <div className="form-buttons">
          <Button
            shape="round"
            className="cancel-button"
            onClick={handleCancel}
          >
            No, Cancel
          </Button>
          <Button
            shape="round"
            type="primary"
            className="save-button"
            onClick={handleOk}
          >
            Yes, Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DeleteFormFieldRecordModal;
